/* =====================================================================
 * TokenExhaustedModal — 체험 토큰 소진 시 정식 구독 전환 유도 모달
 * ===================================================================== */

import React, { useState, useCallback, useMemo } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from '@tanstack/react-query';
import { AlertTriangle } from 'lucide-react';
import { Modal } from '@auw/workspace-ui';
import { useUserStore } from '@auw/stores';
import { useUIStore } from '@auw/stores';
import { useFeature } from '@auw/feature-flags';
import {
  trialUpgradeCheckout,
  getBillingCatalog,
  type BillingCatalog,
} from '@auw/api-client';
import { openPaddleCheckout } from '@auw/workspace-utils';
import { queryKeys } from '@auw/workspace-utils';
import { modeToPath } from '@auw/workspace-utils';
import { PLANS } from '@auw/workspace-utils';
import {
  resolvePlanPricing,
  formatCurrencyAmount,
} from '@auw/api-client';
import type { BillingCycle } from '@auw/workspace-utils';

/* ------------------------------------------------------------------ */
/* Styles                                                              */
/* ------------------------------------------------------------------ */

const Content = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const IconRow = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  color: ${({ theme }) => theme.colors.usageBarCritical};
`;

const Description = styled.p`
  margin: 0;
  font-size: 14px;
  line-height: 1.6;
  color: ${({ theme }) => theme.colors.textSecondary};
`;

const TrialBadge = styled.span`
  display: inline-block;
  font-size: 12px;
  font-weight: 500;
  padding: 3px 10px;
  border-radius: 12px;
  background: ${({ theme }) =>
    theme.mode === 'dark' ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.05)'};
  color: ${({ theme }) => theme.colors.textMuted};
`;

const CycleToggle = styled.div`
  display: flex;
  gap: 8px;
  border-radius: 10px;
  padding: 4px;
  background: ${({ theme }) =>
    theme.mode === 'dark' ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.04)'};
`;

const CycleButton = styled.button<{ $active: boolean }>`
  flex: 1;
  padding: 8px 0;
  border-radius: 8px;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.15s ease;
  background: ${({ theme, $active }) =>
    $active
      ? (theme.mode === 'dark' ? 'rgba(255,255,255,0.12)' : '#fff')
      : 'transparent'};
  color: ${({ theme, $active }) =>
    $active ? theme.colors.textPrimary : theme.colors.textMuted};
  box-shadow: ${({ $active }) =>
    $active ? '0 1px 3px rgba(0,0,0,0.08)' : 'none'};

  &:hover {
    color: ${({ theme }) => theme.colors.textPrimary};
  }
`;

const PriceDisplay = styled.div`
  text-align: center;
  padding: 12px 0;
`;

const PriceAmount = styled.span`
  font-size: 28px;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.textPrimary};
`;

const PriceSuffix = styled.span`
  font-size: 14px;
  color: ${({ theme }) => theme.colors.textMuted};
  margin-left: 2px;
`;

const Actions = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const PrimaryButton = styled.button`
  width: 100%;
  padding: 12px 0;
  border-radius: 10px;
  font-size: 15px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.15s ease;
  background: ${({ theme }) =>
    theme.mode === 'dark' ? '#fff' : '#0d0d0d'};
  color: ${({ theme }) =>
    theme.mode === 'dark' ? '#0d0d0d' : '#fff'};

  &:hover:not(:disabled) {
    opacity: 0.85;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const SecondaryButton = styled.button`
  width: 100%;
  padding: 10px 0;
  border-radius: 10px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.15s ease;
  background: transparent;
  color: ${({ theme }) => theme.colors.textMuted};

  &:hover {
    background: ${({ theme }) =>
      theme.mode === 'dark' ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.04)'};
    color: ${({ theme }) => theme.colors.textPrimary};
  }
`;

/* ------------------------------------------------------------------ */
/* Component                                                           */
/* ------------------------------------------------------------------ */

interface TokenExhaustedModalProps {
  open: boolean;
  onClose: () => void;
  trialEndsAt?: string | null;
}

export const TokenExhaustedModal: React.FC<TokenExhaustedModalProps> = ({
  open,
  onClose,
  trialEndsAt,
}) => {
  const { t } = useTranslation('common');
  const queryClient = useQueryClient();
  const loadUser = useUserStore((s) => s.loadUser);
  const addToast = useUIStore((s) => s.addToast);
  /* P11-5d: SaaS-only Pro upsell. On-prem tenants disable
   * `billing.trialUpsell` so this modal never opens. */
  const trialUpsellEnabled = useFeature('billing.trialUpsell');

  const [cycle, setCycle] = useState<BillingCycle>('yearly');
  const [loading, setLoading] = useState(false);
  const [catalog, setCatalog] = useState<BillingCatalog | null>(null);
  const [catalogFetched, setCatalogFetched] = useState(false);

  const trialDaysRemaining = useMemo(() => {
    if (!trialEndsAt) return null;
    const diff = new Date(trialEndsAt).getTime() - Date.now();
    return Math.max(0, Math.ceil(diff / (1000 * 60 * 60 * 24)));
  }, [trialEndsAt]);

  const pricing = useMemo(() => {
    const proPlan = PLANS.pro;
    return resolvePlanPricing(proPlan, catalog);
  }, [catalog]);

  const yearlyDiscountPercent = useMemo(() => {
    if (pricing.monthlyAmount <= 0) return 0;
    const yearlyPerMonth = pricing.yearlyMonthlyEquivalent;
    return Math.round((1 - yearlyPerMonth / pricing.monthlyAmount) * 100);
  }, [pricing]);

  const displayAmount = cycle === 'yearly'
    ? pricing.yearlyMonthlyEquivalent
    : pricing.monthlyAmount;

  const fetchCatalogOnce = useCallback(async () => {
    if (catalogFetched) return;
    setCatalogFetched(true);
    try {
      const data = await queryClient.fetchQuery({
        queryKey: queryKeys.billing.catalog(),
        queryFn: getBillingCatalog,
        staleTime: 5 * 60 * 1000,
      });
      setCatalog(data);
    } catch {
      // 카탈로그 실패 시 PLANS 폴백값 사용
    }
  }, [catalogFetched, queryClient]);

  const handleOpen = useCallback(() => {
    void fetchCatalogOnce();
  }, [fetchCatalogOnce]);

  const handleSubscribe = useCallback(async () => {
    setLoading(true);
    try {
      const upgrade = await trialUpgradeCheckout({ plan: 'pro', cycle });
      if (upgrade.transaction_id) {
        await openPaddleCheckout(upgrade.transaction_id, {
          onCompleted: () => {
            addToast({
              type: 'success',
              title: t('pricing.paymentComplete', { ns: 'widget' }),
              description: t('pricing.redirectingSoon', { ns: 'widget' }),
            });
            void loadUser();
            void queryClient.invalidateQueries({ queryKey: queryKeys.billing.status() });
            onClose();
            // Phase 11-3a: tenant 의 agent 랜딩으로 경유 (B2C=/agents).
            window.setTimeout(() => window.location.assign(modeToPath('agent')), 2000);
          },
          onClosed: () => {
            setLoading(false);
          },
        });
      } else if (upgrade.checkout_url) {
        window.location.href = upgrade.checkout_url;
      }
    } catch (err) {
      setLoading(false);
      addToast({
        type: 'error',
        title: t('pricing.checkoutFailed', { ns: 'widget' }),
        description: err instanceof Error ? err.message : '',
      });
    }
  }, [cycle, addToast, loadUser, queryClient, onClose, t]);

  return (
    <Modal
      open={open && trialUpsellEnabled}
      onClose={onClose}
      title={t('trialTokensExhausted.title')}
      width="420px"
      closeOnBackdrop={!loading}
    >
      {open && <ModalContent
        trialDaysRemaining={trialDaysRemaining}
        cycle={cycle}
        setCycle={setCycle}
        loading={loading}
        displayAmount={displayAmount}
        currencyCode={pricing.currencyCode}
        yearlyDiscountPercent={yearlyDiscountPercent}
        onSubscribe={handleSubscribe}
        onClose={onClose}
        onMount={handleOpen}
      />}
    </Modal>
  );
};

interface ModalContentProps {
  trialDaysRemaining: number | null;
  cycle: BillingCycle;
  setCycle: (c: BillingCycle) => void;
  loading: boolean;
  displayAmount: number;
  currencyCode: string;
  yearlyDiscountPercent: number;
  onSubscribe: () => void;
  onClose: () => void;
  onMount: () => void;
}

const ModalContent: React.FC<ModalContentProps> = ({
  trialDaysRemaining,
  cycle,
  setCycle,
  loading,
  displayAmount,
  currencyCode,
  yearlyDiscountPercent,
  onSubscribe,
  onClose,
  onMount,
}) => {
  const { t } = useTranslation('common');

  React.useEffect(() => {
    onMount();
  }, [onMount]);

  return (
    <Content>
      <IconRow>
        <AlertTriangle size={20} />
        {trialDaysRemaining !== null && trialDaysRemaining > 0 && (
          <TrialBadge>
            {t('trialTokensExhausted.trialDaysRemaining', { days: trialDaysRemaining })}
          </TrialBadge>
        )}
      </IconRow>

      <Description>{t('trialTokensExhausted.description')}</Description>

      <CycleToggle>
        <CycleButton
          $active={cycle === 'monthly'}
          onClick={() => setCycle('monthly')}
        >
          {t('trialTokensExhausted.monthly')}
        </CycleButton>
        <CycleButton
          $active={cycle === 'yearly'}
          onClick={() => setCycle('yearly')}
        >
          {yearlyDiscountPercent > 0
            ? t('trialTokensExhausted.yearlyDiscount', { percent: yearlyDiscountPercent })
            : t('trialTokensExhausted.yearly')}
        </CycleButton>
      </CycleToggle>

      <PriceDisplay>
        <PriceAmount>
          {formatCurrencyAmount(displayAmount, currencyCode)}
        </PriceAmount>
        <PriceSuffix>{t('trialTokensExhausted.perMonth')}</PriceSuffix>
      </PriceDisplay>

      <Actions>
        <PrimaryButton
          disabled={loading}
          onClick={onSubscribe}
        >
          {loading
            ? t('trialTokensExhausted.subscribing')
            : t('trialTokensExhausted.subscribe')}
        </PrimaryButton>
        <SecondaryButton onClick={onClose} disabled={loading}>
          {t('trialTokensExhausted.later')}
        </SecondaryButton>
      </Actions>
    </Content>
  );
};
